import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Form as FinalForm, Field } from 'react-final-form';
import { Modal, Button, FieldTextInput, FieldSelect } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';
import { categorizeTransactions } from '../../../containers/TransactionPage/helpers';
import css from './PickupModal.css';
import SuspiciousTransactionsStep from './steps/SuspiciousTransactionsStep';
import SignatureStep from './steps/SignatureStep';

const validatePesel = (value) => {
  if (!value) return "Podaj numer PESEL";
  if (!/^\d{11}$/.test(value)) return "PESEL musi składać się z 11 cyfr";

  const weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
  let sum = 0;

  for (let i = 0; i < 10; i++) {
    sum += parseInt(value[i], 10) * weights[i];
  }

  const controlDigit = (10 - (sum % 10)) % 10;

  if (controlDigit !== parseInt(value[10], 10)) {
    return "Niepoprawny numer PESEL (błędna suma kontrolna)";
  }

  return undefined;
};

const PeselModal = ({ transaction, isOpen, onClose, onSubmit, listings, onManageDisableScrolling, handleSignProtocol,
  protocolType, txId, protocol, intl, signProtocolInProgress, signProtocolSuccess, signProtocolError, duplicatedPeselTransactions }) => {
  const [step, setStep] = useState('form');
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const currentUser = useSelector(state => state.user.currentUser)

  const handleFormSubmit = (values) => {
    const { pesel, personalNumberType, ...rest } = values;
    // Usuń klucze partsVerified-... z remarks
    const remarks = Object.fromEntries(
      Object.entries(rest).filter(([key]) => !key.startsWith('partsVerified-'))
    );
    setSubmitInProgress(true);
    onSubmit({ pesel, personalNumberType, remarks })
      .then(res => {
        // console.log(res)
        // const otherTxs = res.filter(tx => tx.relationships.user.data.id.uuid !== transaction.user.id.uuid);
        const { ongoing, overdue, notPaidServiceFees } = categorizeTransactions(res, txId);
        const customerTransactions = [...ongoing, ...overdue, ...notPaidServiceFees];
        setSubmitInProgress(false);
        setStep((customerTransactions?.length > 0 && protocolType === 'pickup') ? 'duplicates' : 'signature')
      })
      .catch(() => setSubmitInProgress(false));
  };

  const inspectionNoteKey = protocolType === 'pickup'
    ? 'pickupInspectionNote'
    : 'returnInspectionNote';

  const defaultRemarks = listings.reduce((acc, listing) => {
    acc[`remarks-${listing.id.uuid}`] = listing.meta?.[inspectionNoteKey] || '';
    return acc;
  }, {});

  const { ongoing, overdue, notPaidServiceFees } = categorizeTransactions(duplicatedPeselTransactions, txId);
  const customerTransactions = [...ongoing, ...overdue, ...notPaidServiceFees];
  const hasDuplicates = customerTransactions && customerTransactions.length > 0;

  return (
    <Modal
      id="PeselModal"
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setStep('form');
      }}
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <div className={css.modalContent}>
        {
          (protocolType === 'pickup' && protocol.pickupSigned) || (protocolType !== 'pickup' && protocol.returnSigned) ? (
            <div>
              Protokół podpisany
            </div>
          )
            :
            (
              <>
                {step === 'form' ? (
                  <>
                    <FinalForm
                      onSubmit={handleFormSubmit}
                      initialValues={{
                        personalNumberType: protocol.personalNumberType || 'pesel',
                        ...defaultRemarks
                      }}
                      render={({ handleSubmit, values, invalid }) => {
                        const labelKey = `PickupModal.label.${values.personalNumberType}`;
                        const inputLabel = intl.formatMessage({ id: labelKey });
                        // Sprawdź, czy wszystkie wymagane checkboxy są zaznaczone
                        const allPartsChecked = listings
                          .filter(listing => {
                            const partsText = listing.attributes.parts;
                            return partsText && partsText.trim() !== '';
                          })
                          .every(listing => values[`partsVerified-${listing.id.uuid}`]);
                        return (
                          <form onSubmit={handleSubmit} className={css.form} autoComplete='off'>
                            {protocolType === 'pickup' && (
                              <>
                                <FieldSelect
                                  name="personalNumberType"
                                  id="personalNumberType"
                                  label={intl.formatMessage({ id: "PickupModal.typeSelect" })}
                                  required
                                >
                                  <option value="" disabled>
                                    {intl.formatMessage({ id: "PickupModal.typeSelectPlaceholder" })}
                                  </option>
                                  <option value="pesel">
                                    {intl.formatMessage({ id: "PickupModal.type.pesel" })}
                                  </option>
                                  <option value="idCard">
                                    {intl.formatMessage({ id: "PickupModal.type.idCard" })}
                                  </option>
                                  <option value="idPassport">
                                    {intl.formatMessage({ id: "PickupModal.type.idPassport" })}
                                  </option>
                                  <option value="other">
                                    {intl.formatMessage({ id: "PickupModal.type.other" })}
                                  </option>
                                </FieldSelect>
                                <FieldTextInput
                                  name="pesel"
                                  id="pesel"
                                  type="text"
                                  label={inputLabel}
                                  placeholder={inputLabel}
                                  validate={(value, allValues) => {
                                    // mamy zapisany juz identyfikator
                                    if (protocol.protocolPersonalNumber) {
                                      // dodatkowo puste pole i nie zmienił się typ identyfikatora
                                      if (!value && allValues.personalNumberType === protocol.personalNumberType) return undefined
                                      // dodatkowo puste pole i zmienił się typ identyfikatora
                                      if (!value && allValues.personalNumberType !== protocol.personalNumberType) {
                                        // puste pole i zmiana typu -> zwaliduj pesel lub czy jest
                                        return allValues.personalNumberType === 'pesel' ? validatePesel(value) : 'Podaj numer identyfikatora';
                                      }

                                      if (value) return allValues.personalNumberType === 'pesel' ? validatePesel(value) : undefined;
                                    }

                                    return allValues.personalNumberType === 'pesel' ? validatePesel(value) : !value ? 'Podaj numer identyfikatora' : undefined;
                                  }}
                                />
                                {protocol.protocolPersonalNumber && (
                                  <div style={{ marginTop: '-10px', color: '#3643BA', fontSize: '13px' }}>
                                    <FormattedMessage id="PickupModal.personalNumberAlreadyProvided" defaultMessage={`${intl.formatMessage({ id: `PickupModal.label.${protocol.personalNumberType}` })} został już wcześniej podany. Możesz go edytować lub zmienić typ identyfikatora.`} />
                                  </div>
                                )}
                              </>
                            )}

                            <h3 className={css.remarksTitle}>Uwagi dotyczące {protocolType === 'pickup' ? 'wydawanych' : 'zwracanych'} produktów</h3>
                            {listings.map((listing) => {
                              const partsText = listing.attributes.parts;
                              const partsList = partsText && partsText.trim() !== '' ? partsText.split('\n').filter(line => line.trim() !== '') : [];
                              const showParts = partsList.length > 0;
                              return (
                                <div key={listing.id.uuid} className={css.listingItem}>
                                  <FieldTextInput
                                    name={`remarks-${listing.id.uuid}`}
                                    type="textarea"
                                    id={`remarks-${listing.id.uuid}`}
                                    label={listing.attributes.title}
                                    placeholder="Uwagi dotyczące produktu"
                                  />
                                  {protocolType !== 'pickup' && listing.meta?.pickupInspectionNote && (
                                    <div className={css.existingNote}>
                                      <span>Uwagi podczas wydania:</span> {listing.meta.pickupInspectionNote}
                                    </div>
                                  )}
                                  {showParts && (
                                    <div className={css.partsList}>
                                      <strong>Części do sprawdzenia:</strong>
                                      <ul>
                                        {partsList.map((part, idx) => (
                                          <li key={idx}>{part}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {showParts && (
                                    <>
                                      <div className={css.partsCheckboxRow}>
                                        <Field
                                          name={`partsVerified-${listing.id.uuid}`}
                                          type="checkbox"
                                          component="input"
                                          id={`partsVerified-${listing.id.uuid}`}
                                        />
                                        <label htmlFor={`partsVerified-${listing.id.uuid}`}>
                                          Zweryfikowałem wszystkie dodatkowe części
                                        </label>
                                      </div>
                                      <p className={css.partsNote}>Jeśli nie wydajesz jakiegoś elementu, wpisz to w uwagach!</p>
                                    </>
                                  )}
                                </div>
                              );
                            })}

                            <div className={css.actions}>
                              <Button type="submit" disabled={invalid || !allPartsChecked} inProgress={submitInProgress}>Dalej</Button>
                              <Button type="button" onClick={onClose}>
                                Anuluj
                              </Button>
                            </div>
                          </form>
                        );
                      }}
                    />

                  </>
                ) : step === 'duplicates' ? (
                  <SuspiciousTransactionsStep
                    customerTransactions={customerTransactions}
                    onBack={() => setStep('form')}
                    onNext={() => setStep('signature')}
                    txId={txId}
                    currentUser={currentUser}
                    transaction={transaction}
                  />
                ) : (
                  <SignatureStep
                    signProtocolInProgress={signProtocolInProgress}
                    signProtocolSuccess={signProtocolSuccess}
                    signProtocolError={signProtocolError}
                    handleSignProtocol={handleSignProtocol}
                    protocolType={protocolType}
                    txId={txId}
                    protocol={protocol}
                    onBack={() => setStep(hasDuplicates ? 'duplicates' : 'form')}
                  />
                )}
              </>
            )
        }
      </div>
    </Modal >
  );
};

export default PeselModal;